import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit, Trash2 } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { Badge } from '@components/Card';
import { PermissionGuard } from '@components/PermissionGuard';
import { vehicleService } from '@services/vehicleService';
import { Vehicle } from '@/types/vehicle';

export const VehicleListView: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const pageSize = 20;

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await vehicleService.getAll({
        page: currentPage,
        pageSize,
        searchName: search.trim() || undefined,
      });
      setItems(result.data);
      setTotalPages(result.pagination.totalPages);
      setTotalItems(result.pagination.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [currentPage]);

  const handleSearch = () => {
    setCurrentPage(1);
    loadItems();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleDelete = async (id: number) => {
    try {
      await vehicleService.delete(id);
      setDeleteConfirm(null);
      loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-h1">Veículos</h1>
          <p className="text-slate-600">Gerencie os veículos cadastrados no sistema</p>
        </div>
        <PermissionGuard permission="vehicles.write">
          <Button onClick={() => navigate('/settings/transport/vehicles/new')} icon={<Plus size={20} />}>
            Novo Veículo
          </Button>
        </PermissionGuard>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <Input
              placeholder="Buscar por descrição ou placa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Button onClick={handleSearch} variant="secondary" icon={<Search size={18} />}>
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <tr>
                <th className="text-left">ID</th>
                <th className="text-left">Descrição</th>
                <th className="text-left">Placa</th>
                <th className="text-left">Renavan</th>
                <th className="text-left">IMEI Rastreador</th>
                <th className="text-center">Status</th>
                <th className="text-center">Ações</th>
              </tr>
            </TableHeader>
            <tbody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    Nenhum veículo encontrado
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.vehicle_id}>
                    <TableCell className="font-mono text-sm">{item.vehicle_id}</TableCell>
                    <TableCell className="font-medium">{item.description}</TableCell>
                    <TableCell className="font-mono">{item.license_plate}</TableCell>
                    <TableCell>{item.renavan || '—'}</TableCell>
                    <TableCell>{item.tracker_imei || '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant={item.is_active ? 'success' : 'neutral'}>
                          {item.is_active ? 'Ativo' : 'Inativo'}
                        </Badge>
                        {item.is_executive && (
                          <Badge variant={item.delivery_enabled ? 'info' : 'warning'}>
                            {item.delivery_enabled ? 'Diretoria · liberado' : 'Diretoria · não liberado'}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <PermissionGuard permission="vehicles.write">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/settings/transport/vehicles/${item.vehicle_id}/edit`)}
                            icon={<Edit size={16} />}
                          />
                        </PermissionGuard>
                        <PermissionGuard permission="vehicles.write">
                          {deleteConfirm === item.vehicle_id ? (
                            <div className="flex items-center gap-1">
                              <Button variant="danger" size="sm" onClick={() => handleDelete(item.vehicle_id)}>
                                Confirmar
                              </Button>
                              <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                                Cancelar
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setDeleteConfirm(item.vehicle_id)}
                              icon={<Trash2 size={16} className="text-red-500" />}
                            />
                          )}
                        </PermissionGuard>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </tbody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">
            {totalItems} registro(s) encontrado(s)
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="flex items-center px-3 text-sm text-slate-600">
              Página {currentPage} de {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
